import React, { useEffect, useMemo, useState } from "react";
import { Player, SavedTournament } from "../../types.ts";
import { useAuth } from "../../context/AuthContext.tsx";
import { updateResult } from "../../utils/firebase-utils.js";
import { getParticipantCount, getWinnerName } from "../../utils/championship.ts";
import TournamentResultTable from "../TournamentResultTable.tsx";

interface TournamentHistoryProps {
  tournaments: SavedTournament[];
  allPlayers: Player[];
  /** Zápis popisu/komentáře zpět do seznamu po uložení. */
  onTournamentUpdated: (tournament: SavedTournament) => void;
}

/** Porovnání bez ohledu na diakritiku a velikost písmen. */
const normalize = (text: string): string =>
  (text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const TournamentHistory: React.FC<TournamentHistoryProps> = ({
  tournaments,
  allPlayers,
  onTournamentUpdated,
}) => {
  const { currentUser } = useAuth();
  const [query, setQuery] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [descriptionDraft, setDescriptionDraft] = useState<string>("");
  const [commentDraft, setCommentDraft] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string>("");

  const selected = tournaments.find((t) => t.id === selectedId) || null;

  // Po otevření turnaje načteme jeho texty do formuláře
  useEffect(() => {
    setDescriptionDraft(selected?.description || "");
    setCommentDraft(selected?.comment || "");
    setSaveError("");
  }, [selected]);

  useEffect(() => {
    if (!selected) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedId(null);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [selected]);

  /** Hledá se podle data, popisu, komentáře i jmen hráčů. */
  const filtered = useMemo(() => {
    const needle = normalize(query.trim());
    if (!needle) return tournaments;

    return tournaments.filter((tournament) => {
      const names = (tournament.playerNames || [])
        .filter(Boolean)
        .join(" ");
      const fallbackNames = (tournament.player || [])
        .map((id) => allPlayers.find((p) => p.id === id)?.player_name || "")
        .join(" ");

      const haystack = normalize(
        [
          tournament.date,
          tournament.description || "",
          tournament.comment || "",
          names,
          fallbackNames,
        ].join(" ")
      );

      return haystack.includes(needle);
    });
  }, [tournaments, allPlayers, query]);

  const handleSaveTexts = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError("");
    try {
      await updateResult(selected.id, {
        description: descriptionDraft,
        comment: commentDraft,
      });
      onTournamentUpdated({
        ...selected,
        description: descriptionDraft,
        comment: commentDraft,
      });
    } catch (err) {
      console.error("Chyba při ukládání popisu turnaje:", err);
      setSaveError("Uložení se nepodařilo. Zkuste to prosím znovu.");
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedTexts =
    selected !== null &&
    (descriptionDraft !== (selected.description || "") ||
      commentDraft !== (selected.comment || ""));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            ⌕
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hledat podle data, hráče nebo popisu…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <span className="text-sm text-slate-500 shrink-0">
          {filtered.length} z {tournaments.length}{" "}
          {tournaments.length === 1 ? "turnaje" : "turnajů"}
        </span>
      </div>

      {filtered.length === 0 ? (
        <p className="text-slate-500 py-8 text-center">
          {tournaments.length === 0
            ? "Zatím tu nejsou žádné odehrané turnaje."
            : `Hledání „${query.trim()}" nic nenašlo.`}
        </p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((tournament) => {
            const winner = getWinnerName(tournament, allPlayers);
            const participants = getParticipantCount(tournament);

            return (
              <li key={tournament.id}>
                <button
                  onClick={() => setSelectedId(tournament.id)}
                  className="w-full h-full text-left rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-blue-300 transition-all"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-slate-900 tabular-nums">
                      {tournament.date}
                    </span>
                    <span className="text-xs text-slate-500">
                      {participants} hráčů
                    </span>
                  </div>

                  {winner && (
                    <div className="mt-2 flex items-center gap-1.5 text-sm">
                      <span aria-hidden="true">🏆</span>
                      <span className="font-medium text-slate-700 truncate">
                        {winner}
                      </span>
                    </div>
                  )}

                  {tournament.description && (
                    <p className="mt-2 text-sm text-slate-500 line-clamp-2">
                      {tournament.description}
                    </p>
                  )}

                  <span className="mt-3 inline-block text-sm font-medium text-blue-600">
                    Zobrazit výsledky →
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Detail turnaje */}
      {selected && (
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setSelectedId(null)}
          />

          <div className="relative min-h-screen flex items-start justify-center p-4">
            <div className="relative w-full max-w-5xl my-6 rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    Turnaj {selected.date}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {getParticipantCount(selected)} hráčů
                    {getWinnerName(selected, allPlayers)
                      ? ` · vítěz ${getWinnerName(selected, allPlayers)}`
                      : ""}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedId(null)}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <span className="sr-only">Zavřít</span>
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-5 space-y-6">
                {/* Popis a komentář */}
                {currentUser ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3">
                      Popis a komentář
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          Popis turnaje
                        </label>
                        <textarea
                          value={descriptionDraft}
                          onChange={(e) => setDescriptionDraft(e.target.value)}
                          rows={2}
                          placeholder="Např. Vánoční turnaj, hrálo se na 5 minut…"
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          Komentář
                        </label>
                        <textarea
                          value={commentDraft}
                          onChange={(e) => setCommentDraft(e.target.value)}
                          rows={3}
                          placeholder="Jak turnaj probíhal, zajímavé partie…"
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-3">
                      <button
                        onClick={handleSaveTexts}
                        disabled={saving || !hasUnsavedTexts}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          saving || !hasUnsavedTexts
                            ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                      >
                        {saving ? "Ukládám…" : "Uložit"}
                      </button>
                      {hasUnsavedTexts && !saving && (
                        <span className="text-xs text-slate-500">
                          Máte neuložené změny
                        </span>
                      )}
                      {saveError && (
                        <span className="text-xs text-rose-600">
                          {saveError}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  (selected.description || selected.comment) && (
                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-2">
                      {selected.description && (
                        <p className="text-slate-800 font-medium">
                          {selected.description}
                        </p>
                      )}
                      {selected.comment && (
                        <p className="text-sm text-slate-600 whitespace-pre-line">
                          {selected.comment}
                        </p>
                      )}
                    </div>
                  )
                )}

                <TournamentResultTable
                  tournament={selected}
                  allPlayers={allPlayers}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentHistory;
